import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { GraduationCap } from "lucide-react";
import Sidebar from "./Sidebar";
import BackToTop from "./BackToTop";
import LumosWidget from "@/components/lumos/LumosWidget";
import { useAuth } from "@/contexts/AuthContext";
import { startDemoTour, waitForEl, FIRST_STEP_SELECTOR } from "@/components/plan/DemoTour";
import { isDemoUser, leaveDemoForRegister } from "@/lib/demo";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/constants";

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isDemo = isDemoUser(user);
  const createAccount = () => leaveDemoForRegister(logout, navigate);

  // Sidebar collapse state lives here so sibling UI (e.g. back-to-top) can
  // follow the sidebar width. Storage may be unavailable; never let it throw.
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar-collapsed") === "1";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem("sidebar-collapsed", next ? "1" : "0");
    } catch {
      /* ignore */
    }
  };

  // One-shot auto-tour: /demo sets this flag before navigating in, so outreach
  // visitors get the guided tutorial without clicking anything. It starts only
  // once the first step's target exists, so it never fires before the page is up.
  useEffect(() => {
    if (!isDemo || sessionStorage.getItem("elementar_auto_tour") !== "1") return;
    sessionStorage.removeItem("elementar_auto_tour");
    let cancelled = false;
    waitForEl(FIRST_STEP_SELECTOR, 10000).then((el) => {
      if (el && !cancelled) startDemoTour(navigate, createAccount);
    });
    return () => {
      cancelled = true;
    };
    // createAccount closes over logout/navigate only; run once per demo session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemo, navigate]);

  // Publish the sticky header's height so sticky children (table headers) sit below it.
  const rootRef = useRef(null);
  const headerRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const set = () =>
      root.style.setProperty("--app-header-h", `${headerRef.current?.offsetHeight || 0}px`);
    set();
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isDemo]);

  return (
    <div ref={rootRef} className="min-h-screen bg-gray-50">
      <Sidebar collapsed={collapsed} onToggle={toggleCollapsed} />
      <div
        className={`${
          collapsed ? "lg:ml-16" : "lg:ml-64"
        } flex flex-col min-h-screen transition-[margin] duration-200`}
      >
        {/* Top bar — only rendered for the demo tenant */}
        {isDemo && (
          <header ref={headerRef} className="sticky top-0 z-20 flex items-center justify-between gap-4 px-6 py-2 bg-white border-b border-gray-100 shadow-sm">
            {/* The introduction lives here, not in a modal. A first-time visitor
                arrives from an outreach link with no idea what they are looking
                at, so something must say it — but a centered dialog says it on
                top of the very numbers it is describing. This bar is sticky, so
                the explanation stays available while the book stays visible. */}
            <p className="text-sm text-gray-600 leading-snug">
              You're exploring a sample family office. Every figure was read automatically from
              investor-portal documents.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => startDemoTour(navigate, createAccount)}
                className="flex items-center gap-2 text-sm font-medium"
              >
                <GraduationCap className="w-4 h-4" />
                Tutorial
              </Button>
              <Button
                size="sm"
                onClick={createAccount}
                className="text-sm font-semibold text-white"
                style={{ backgroundColor: BRAND.bg }}
              >
                Create your account
              </Button>
            </div>
          </header>
        )}
        <main className="flex-1">
          <div className="p-6 lg:p-8">
            <Outlet />
          </div>
        </main>
      </div>
      {/* Floating client chatbot — available on every authenticated page. */}
      <LumosWidget />
      <BackToTop collapsed={collapsed} />
    </div>
  );
}
